// Thin Cleanup wrapper around the existing ContactMoveHouseholdModal
// (search → pick destination household → move) — resolves
// contact-no-household's `move` recommendation, which had no decide-rail
// handling at all before this (see contact-move-predicate.ts). The
// modal needs a full SafeContact, not just a RecordRef, so this fetches
// it first; currentHouseholdName is always null here since these are by
// definition contacts with no household.

import { useEffect, useState } from "react";

import { api, ApiError, type SafeContact } from "../api";
import { ContactMoveHouseholdModal } from "../components/ContactMoveHouseholdModal";
import { Modal } from "../components/Modal";
import { Button, ErrorBanner, LoadingBlock } from "../ui";
import type { RecordRef } from "./cleanup-records";

export function CleanupContactMoveOverlay({
  source,
  onClose,
  onMoved,
}: {
  source: RecordRef;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [contact, setContact] = useState<SafeContact | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setContact(null);
    void (async () => {
      try {
        const res = await api.getContact(source.id);
        if (!cancelled) setContact(res.contact);
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? e.message
            : "Couldn't load this contact's record.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source.id]);

  if (error) {
    return (
      <Modal open={true} onClose={onClose} title="Couldn't open this record">
        <ErrorBanner message={error} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button variant="sw-default" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  if (!contact) {
    return (
      <Modal open={true} onClose={onClose} title="Loading…">
        <LoadingBlock>Loading contact…</LoadingBlock>
      </Modal>
    );
  }

  return (
    <ContactMoveHouseholdModal
      contact={contact}
      currentHouseholdName={null}
      onClose={onClose}
      onMoved={onMoved}
    />
  );
}
